import { IconProps } from '@mui/material';
import { RiArrowDropDownLine } from '@remixicon/react';
import { useLocation } from '@tanstack/react-router';
import { AgButton, AgText, AgTooltip } from 'src/components/ui';
import { cn } from 'src/components/ui/utils';
import { useSidebar } from '../../../providers/SidebarContextProvider';

export type SidebarLinkItem = {
  icon?: React.FC<IconProps>;
  href?: string;
  onClick?: () => void;
  text: string;
  parent?: boolean;
  nested?: boolean;
  testId?: string;
  disabled?: boolean;
  current?: boolean;
};

export type SidebarLinkProps = SidebarLinkItem;

export default function SidebarLink({
  text,
  icon: Icon,
  href,
  onClick,
  parent,
  nested,
  testId,
  disabled,
  current,
}: SidebarLinkProps) {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { sidebarOpen } = useSidebar();
  const pathToCheck = nested ? pathname : '/' + pathname.split('/')[1];
  const highlight = current ?? pathToCheck === href;

  const icon = Icon ? (
    <div
      className={cn(
        'center w-5 h-5 [&_svg]:w-full [&_svg]:h-full',
        highlight ? 'text-brand-7' : 'text-neutral-alpha-8'
      )}
    >
      <Icon />
    </div>
  ) : null;

  const button = sidebarOpen ? (
    <AgButton
      isDisabled={disabled}
      variant="ghost"
      className={cn('w-full relative justify-start', highlight && 'bg-brand-1')}
      href={href}
      onClick={onClick}
      data-testid={testId}
    >
      {icon}
      <AgText
        className="ml-2"
        size={nested ? '14' : '13'}
        color={highlight ? 'brand' : nested ? 'secondary' : 'primary'}
        weight="medium"
      >
        {text}
      </AgText>
      <div className="flex-1" />
      {parent && (
        <div
          className={cn(
            'center w-5 h-5 [&_svg]:w-full [&_svg]:h-full',
            highlight ? 'text-brand-7' : 'text-neutral-alpha-8'
          )}
        >
          <RiArrowDropDownLine />
        </div>
      )}
    </AgButton>
  ) : (
    <AgTooltip content={text} placement="right">
      <AgButton
        isDisabled={disabled}
        shape="rounded"
        variant="ghost"
        size="icon-sm"
        className={cn('relative rounded-md', highlight && 'bg-brand-1')}
        href={href}
        onClick={onClick}
        data-testid={testId}
      >
        {icon}
      </AgButton>
    </AgTooltip>
  );

  if (nested) {
    return (
      <div className="flex items-center pl-2 border-l border-neutral-5 ml-3 relative">
        {highlight && <div className="absolute h-6 w-2 rounded-sm bg-brand-5" style={{ left: '-4.5px' }} />}
        {button}
      </div>
    );
  }

  return button;
}
